import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ArrowUp, ArrowDown, Search, X, Pencil, ClipboardCheck, Check } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/**
 * Excel-like data table: click-to-sort, global search, inline cell editing, pagination.
 * - Server mode (default): backend returns {total, items} and handles sort/search/paginate.
 * - Client mode (clientSide=true): endpoint returns an array; sort/search/paginate in-browser.
 *
 * columns: [{ key, label, sortable, editable, type:'text'|'number'|'select'|'date',
 *             options:[{value,label}]|[str], render:(value,row)=>node, mono, width, align }]
 */
export function DataTable({
  endpoint, params = {}, columns, rowKey = "id", editEndpoint, canEdit = false,
  defaultSort = { by: "updated_at", dir: "desc" }, searchPlaceholder = "Search…",
  initialLimit = 25, pageSizeOptions = [25, 50, 100], clientSide = false, onRowClick, toolbar, rightToolbar,
  emptyText = "No records found.", testid = "data-table", refreshSignal = 0, onChanged,
  filterRows, onLoaded,
  selectable = false, bulkToolbar,
  // When true, editable cells don't save on every change — the user clicks
  // "Edit" to unlock the grid, makes any number of changes/copy-pastes
  // (staged locally, nothing hits the server), then clicks "Save changes"
  // to write them all at once. Good for a spreadsheet-style bulk-editing
  // session instead of one network round-trip per cell.
  batchEdit = false,
  // Fires with the debounced per-column filter map ({ col_key: value })
  // whenever it settles — lets a parent page react to a specific column's
  // filter text (e.g. show a summary derived from the current filter).
  onColSearchChange,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [colSearch, setColSearch] = useState({}); // { col_key: value }
  const [colDebounced, setColDebounced] = useState({});
  const [sort, setSort] = useState(defaultSort);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(initialLimit);
  const [edit, setEdit] = useState(null); // {id, key, value}
  const [selected, setSelected] = useState(() => new Set());
  const [clip, setClip] = useState(null); // {rowId, colKey, value} — Excel-like copy/paste on editable cells
  const [editModeOn, setEditModeOn] = useState(false); // batchEdit: is the grid currently unlocked?
  const [pending, setPending] = useState({}); // batchEdit: { rowId: { colKey: value } } — staged, unsaved
  const effectiveCanEdit = batchEdit ? (canEdit && editModeOn) : canEdit;
  const paramKey = JSON.stringify(params);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setColDebounced(colSearch), 300);
    return () => clearTimeout(t);
  }, [colSearch]);

  useEffect(() => { onColSearchChange && onColSearchChange(colDebounced); }, [colDebounced]); // eslint-disable-line

  const colKey = JSON.stringify(colDebounced);
  // Reset to page 1 when the user changes a search/filter/page-size — but
  // NOT on a plain data refresh (refreshSignal bump after an edit/save),
  // which must re-fetch the SAME page in place, not jump the user back to
  // the top of the list.
  useEffect(() => { setSkip(0); }, [debounced, paramKey, colKey, limit]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const q = { ...params };
    // Per-column "Excel-like" filter values get sent as <col_key>_q
    Object.entries(colDebounced).forEach(([k, v]) => { if (v) q[`${k}_q`] = v; });
    if (clientSide) {
      api.get(endpoint, { params: q })
        .then((r) => { const arr = Array.isArray(r.data) ? r.data : (r.data.items || []); setRows(arr); setTotal(arr.length); onLoaded && onLoaded(arr); })
        .catch((e) => toast.error(apiErr(e)))
        .finally(() => setLoading(false));
    } else {
      q.search = debounced || undefined;
      q.sort_by = sort.by; q.sort_dir = sort.dir; q.limit = limit; q.skip = skip;
      api.get(endpoint, { params: q })
        .then((r) => {
          if (Array.isArray(r.data)) { setRows(r.data); setTotal(r.data.length); onLoaded && onLoaded(r.data); }
          else { const arr = r.data.items || []; setRows(arr); setTotal(r.data.total ?? arr.length); onLoaded && onLoaded(arr); }
        })
        .catch((e) => toast.error(apiErr(e)))
        .finally(() => setLoading(false));
    }
  }, [endpoint, paramKey, debounced, sort.by, sort.dir, skip, limit, clientSide, colKey]); // eslint-disable-line

  useEffect(() => { fetchData(); }, [fetchData, refreshSignal]);

  // client-side derived
  const view = useMemo(() => {
    if (!clientSide) return rows;
    let r = [...rows];
    if (filterRows) r = filterRows(r);
    // global search
    if (debounced) {
      const t = debounced.toLowerCase();
      const keys = columns.filter((c) => c.searchable !== false).map((c) => c.key);
      r = r.filter((row) => keys.some((k) => String(row[k] ?? "").toLowerCase().includes(t)));
    }
    // per-column filters
    Object.entries(colDebounced).forEach(([k, v]) => {
      if (!v) return;
      const t = String(v).toLowerCase();
      r = r.filter((row) => String(row[k] ?? "").toLowerCase().includes(t));
    });
    if (sort.by) {
      r.sort((a, b) => {
        const av = a[sort.by], bv = b[sort.by];
        if (av == null) return 1; if (bv == null) return -1;
        const c = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
        return sort.dir === "desc" ? -c : c;
      });
    }
    return r.slice(skip, skip + limit);
  }, [rows, clientSide, debounced, colDebounced, sort, skip, limit, columns, filterRows]);

  // client-side filtered count (before pagination) for accurate pager
  const filteredCount = useMemo(() => {
    if (!clientSide) return total;
    let r = rows;
    if (filterRows) r = filterRows(r);
    if (debounced) {
      const t = debounced.toLowerCase();
      const keys = columns.filter((c) => c.searchable !== false).map((c) => c.key);
      r = r.filter((row) => keys.some((k) => String(row[k] ?? "").toLowerCase().includes(t)));
    }
    Object.entries(colDebounced).forEach(([k, v]) => {
      if (!v) return;
      const t = String(v).toLowerCase();
      r = r.filter((row) => String(row[k] ?? "").toLowerCase().includes(t));
    });
    return r.length;
  }, [rows, clientSide, debounced, colDebounced, columns, filterRows, total]);

  const shownTotal = clientSide ? filteredCount : total;
  const display = clientSide ? view : rows;

  const toggleSort = (key) => {
    setSort((s) => s.by === key ? { by: key, dir: s.dir === "asc" ? "desc" : "asc" } : { by: key, dir: "asc" });
  };

  // A boolean-select column declares options like [{value:"true",...},{value:"false",...}].
  // We auto-detect so editing such columns works correctly with DB booleans.
  const isBooleanSelect = (col) =>
    col.type === "select" && Array.isArray(col.options) && col.options.length === 2 &&
    col.options.every((o) => typeof o === "object" && (o.value === "true" || o.value === "false"));

  const startEdit = (row, col) => {
    if (!effectiveCanEdit || !col.editable || !(col.editEndpoint || editEndpoint)) return;
    const raw = row[col.key];
    const rid = row[rowKey];
    const staged = pending[rid] && Object.prototype.hasOwnProperty.call(pending[rid], col.key);
    const effectiveRaw = staged ? pending[rid][col.key] : raw;
    const initial = isBooleanSelect(col) && typeof effectiveRaw === "boolean" ? String(effectiveRaw) : (effectiveRaw ?? "");
    setEdit({ id: rid, key: col.key, value: initial });
    setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
  };

  // Applies + PATCHes a value immediately (no separate "confirm" step) — used
  // by select-onChange (instant commit), text/date/number onBlur/Enter, and
  // Ctrl+V paste. `rawVal` is the raw string/bool coming from the control.
  const applyValue = async (row, col, rawVal) => {
    let newVal = col.type === "number" ? (rawVal === "" ? null : Number(rawVal)) : rawVal;
    if (isBooleanSelect(col)) newVal = newVal === "true";
    if (newVal === (row[col.key] ?? (col.type === "number" ? null : ""))) { setEdit(null); return; }
    try {
      const endpoint = col.editEndpoint || editEndpoint;
      await api.patch(typeof endpoint === "function" ? endpoint(row) : endpoint, { [col.key]: newVal });
      setRows((rs) => rs.map((r) => r[rowKey] === row[rowKey] ? { ...r, [col.key]: newVal } : r));
      toast.success("Saved — change written to audit log");
      setEdit(null);
      onChanged && onChanged();
      fetchData();
    } catch (e) { toast.error(apiErr(e)); setEdit(null); }
  };
  // batchEdit mode: stage the change locally instead of hitting the server —
  // it's applied (visually, via a "pending change" highlight) only, until
  // the user clicks "Save changes" to flush everything at once.
  const stageValue = (row, col, rawVal) => {
    let newVal = col.type === "number" ? (rawVal === "" ? null : Number(rawVal)) : rawVal;
    if (isBooleanSelect(col)) newVal = newVal === "true";
    const rid = row[rowKey];
    const already = pending[rid] && Object.prototype.hasOwnProperty.call(pending[rid], col.key)
      ? pending[rid][col.key] : (row[col.key] ?? (col.type === "number" ? null : ""));
    setEdit(null);
    if (newVal === already) return;
    setPending((p) => ({ ...p, [rid]: { ...(p[rid] || {}), [col.key]: newVal } }));
  };
  const commitOrStage = (row, col, rawVal) => { batchEdit ? stageValue(row, col, rawVal) : applyValue(row, col, rawVal); };
  const saveEdit = (row, col) => { if (edit) commitOrStage(row, col, edit.value); };

  const pendingCount = Object.values(pending).reduce((n, cols) => n + Object.keys(cols).length, 0);
  const discardPending = () => { setPending({}); toast.message("Unsaved changes discarded"); };
  const saveAllPending = async () => {
    const groups = {};
    Object.entries(pending).forEach(([rid, cols]) => {
      const row = rows.find((r) => String(r[rowKey]) === String(rid));
      if (!row) return;
      Object.entries(cols).forEach(([ck, val]) => {
        const col = columns.find((c) => c.key === ck);
        if (!col) return;
        const ep = col.editEndpoint || editEndpoint;
        const url = typeof ep === "function" ? ep(row) : ep;
        const gKey = `${url}::${rid}`;
        groups[gKey] = groups[gKey] || { url, body: {} };
        groups[gKey].body[ck] = val;
      });
    });
    let ok = 0, fail = 0;
    for (const g of Object.values(groups)) {
      try { await api.patch(g.url, g.body); ok++; } catch (e) { fail++; toast.error(apiErr(e)); }
    }
    setPending({});
    if (ok) toast.success(`Saved ${ok} change${ok === 1 ? "" : "s"}${fail ? ` — ${fail} failed` : ""}`);
    onChanged && onChanged();
    fetchData();
  };

  // Ctrl/Cmd+C copies an editable cell's raw value; Ctrl/Cmd+V on another
  // cell in the SAME column pastes + saves (or stages, in batchEdit mode)
  // immediately — no click-through menus, just like a spreadsheet.
  const copyPasteKeyDown = (e, row, col) => {
    const meta = e.ctrlKey || e.metaKey;
    if (!meta) return;
    const k = e.key.toLowerCase();
    const rid = row[rowKey];
    if (k === "c") {
      e.preventDefault();
      const staged = pending[rid] && Object.prototype.hasOwnProperty.call(pending[rid], col.key);
      const raw = staged ? pending[rid][col.key] : row[col.key];
      setClip({ rowId: rid, colKey: col.key, value: isBooleanSelect(col) ? String(!!raw) : (raw ?? "") });
      try { navigator.clipboard && navigator.clipboard.writeText(String(raw ?? "")).catch(() => {}); } catch (_e) { /* clipboard perms not required — internal paste still works */ }
    } else if (k === "v") {
      e.preventDefault();
      if (!clip) return;
      if (clip.colKey !== col.key) { toast.error("Copy a cell from this same column first"); return; }
      commitOrStage(row, col, clip.value);
    }
  };

  const pendingLabel = (col, val) => {
    if (col.type === "select") {
      const opts = (col.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
      return opts.find((o) => o.value === val)?.label ?? (val === "" || val == null ? "—" : String(val));
    }
    return val === "" || val == null ? "—" : String(val);
  };

  const renderCell = (row, col) => {
    const editing = edit && edit.id === row[rowKey] && edit.key === col.key;
    if (editing) {
      const commit = () => saveEdit(row, col);
      if (col.type === "select") {
        const opts = (col.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
        return (
          <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
            <select ref={inputRef} className="input" style={{ padding: "4px 8px", minWidth: 120 }} value={edit.value || ""}
              autoFocus
              onChange={(e) => { const v = e.target.value; setEdit({ ...edit, value: v }); if (v !== "") commitOrStage(row, col, v); }}
              onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setEdit(null); return; } copyPasteKeyDown(e, row, col); }}
              onBlur={() => setEdit((cur) => (cur && cur.id === row[rowKey] && cur.key === col.key ? null : cur))}
              data-testid={`edit-${col.key}`}>
              <option value="">—</option>
              {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button className="icon-btn" style={{ width: 26, height: 26 }} title="Cancel" onMouseDown={(e) => { e.preventDefault(); setEdit(null); }}><X size={14} /></button>
          </span>
        );
      }
      return (
        <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
          <input ref={inputRef} className="input" style={{ padding: "4px 8px", minWidth: 110 }} type={col.type === "number" ? "number" : col.type === "date" ? "date" : "text"}
            value={edit.value ?? ""} onChange={(e) => setEdit({ ...edit, value: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEdit(null); }}
            onBlur={commit} data-testid={`edit-${col.key}`} />
        </span>
      );
    }
    const rid = row[rowKey];
    const staged = pending[rid] && Object.prototype.hasOwnProperty.call(pending[rid], col.key);
    if (staged) {
      return (
        <span className={col.mono ? "mono" : ""} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff4d6", padding: "2px 7px", borderRadius: 6 }}
          title="Unsaved change — click Save changes to apply">
          {pendingLabel(col, pending[rid][col.key])}
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--gold, #c9a227)", flexShrink: 0 }} />
        </span>
      );
    }
    const content = col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—");
    const editable = effectiveCanEdit && col.editable && (col.editEndpoint || editEndpoint);
    return (
      <span className={`${col.mono ? "mono" : ""}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {content}
        {editable && <Pencil size={11} className="edit-pencil" style={{ opacity: 0, color: "var(--text-3)" }} />}
      </span>
    );
  };

  const hasColSearch = columns.some((c) => c.colSearch);

  // Bulk-selection helpers
  const displayIds = display.map((r) => r[rowKey]);
  const allVisibleSelected = selectable && display.length > 0 && displayIds.every((id) => selected.has(id));
  const toggleAllVisible = () => {
    setSelected((s) => {
      const n = new Set(s);
      if (allVisibleSelected) displayIds.forEach((id) => n.delete(id));
      else displayIds.forEach((id) => n.add(id));
      return n;
    });
  };
  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const clearSelection = () => setSelected(new Set());

  return (
    <div data-testid={testid}>
      {selectable && selected.size > 0 && bulkToolbar && (
        <div className="card pad" data-testid={`${testid}-bulk-toolbar`}
          style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 12,
                   background: "#fff4d6", border: "1px solid #e3d28a" }}>
          <span className="t-strong" style={{ fontSize: 13.5 }}>
            {selected.size} record{selected.size === 1 ? "" : "s"} selected
          </span>
          <span style={{ flex: 1 }} />
          {bulkToolbar(Array.from(selected), clearSelection)}
          <button className="btn ghost sm" onClick={clearSelection} data-testid={`${testid}-bulk-clear`}>Clear</button>
        </div>
      )}
      <div className="ftabs" style={{ alignItems: "center" }}>
        {batchEdit && canEdit && (
          <>
            <button className={`btn sm ${editModeOn ? "" : "ghost"}`} onClick={() => setEditModeOn((v) => !v)}
              data-testid={`${testid}-edit-toggle`} title={editModeOn ? "Lock the grid again" : "Unlock cells for spreadsheet-style bulk editing"}>
              <Pencil size={13} /> {editModeOn ? "Editing…" : "Edit"}
            </button>
            {pendingCount > 0 && (
              <>
                <button className="btn sm" onClick={saveAllPending} data-testid={`${testid}-save-pending`}>
                  <Check size={13} /> Save changes ({pendingCount})
                </button>
                <button className="btn ghost sm" onClick={discardPending} data-testid={`${testid}-discard-pending`}>Discard</button>
              </>
            )}
          </>
        )}
        {toolbar}
        <span style={{ flex: 1 }} />
        {rightToolbar}
        <div className="search" style={{ maxWidth: 300 }}>
          <Search size={15} />
          <input placeholder={searchPlaceholder} value={search} onChange={(e) => setSearch(e.target.value)} data-testid={`${testid}-search`} />
        </div>
      </div>

      <div className="tbl-wrap">
        <table className="dt">
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: 32 }}>
                  <input type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    data-testid={`${testid}-select-all`}
                    aria-label="Select all on this page" />
                </th>
              )}
              {columns.map((col) => (
                <th key={col.key} style={{ width: col.width, textAlign: col.align, cursor: col.sortable ? "pointer" : "default", userSelect: "none" }}
                  onClick={col.sortable ? () => toggleSort(col.key) : undefined} data-testid={`${testid}-th-${col.key}`}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {col.label}
                    {col.sortable && sort.by === col.key && (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                  </span>
                </th>
              ))}
            </tr>
            {hasColSearch && (
              <tr className="col-search-row">
                {selectable && <th style={{ background: "var(--bg-soft, #fafafa)" }}></th>}
                {columns.map((col) => (
                  <th key={col.key} style={{ padding: "4px 8px", background: "var(--bg-soft, #fafafa)" }}>
                    {col.colSearch && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--bg, #fff)", borderRadius: 6, padding: "2px 6px", border: "1px solid var(--line, #eee)" }}>
                        <Search size={11} color="var(--text-3)" />
                        <input
                          value={colSearch[col.key] || ""}
                          onChange={(e) => setColSearch({ ...colSearch, [col.key]: e.target.value })}
                          placeholder={`Filter ${col.label.toLowerCase()}…`}
                          style={{ border: "none", outline: "none", width: "100%", background: "none", fontSize: 11.5, padding: "2px 0" }}
                          data-testid={`${testid}-colsearch-${col.key}`}
                        />
                        {colSearch[col.key] && (
                          <button className="icon-btn" style={{ width: 16, height: 16 }} onClick={() => setColSearch({ ...colSearch, [col.key]: "" })}>
                            <X size={10} />
                          </button>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {loading && <tr><td colSpan={columns.length + (selectable ? 1 : 0)} className="t-mut" style={{ padding: 30, textAlign: "center" }}>Loading…</td></tr>}
            {!loading && display.length === 0 && <tr><td colSpan={columns.length + (selectable ? 1 : 0)} className="t-mut" style={{ padding: 30, textAlign: "center" }}>{emptyText}</td></tr>}
            {!loading && display.map((row) => {
              const isSel = selectable && selected.has(row[rowKey]);
              return (
                <tr key={row[rowKey]} onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{
                    ...(onRowClick ? { cursor: "pointer" } : null),
                    ...(isSel ? { background: "#fff8e6" } : null),
                  }}
                  data-testid={`${testid}-row`}>
                  {selectable && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={isSel}
                        onChange={() => toggleOne(row[rowKey])}
                        data-testid={`${testid}-select-${row[rowKey]}`}
                        aria-label={`Select row ${row[rowKey]}`} />
                    </td>
                  )}
                  {columns.map((col) => {
                    const editingThis = edit && edit.id === row[rowKey] && edit.key === col.key;
                    const isEditableCol = effectiveCanEdit && col.editable && (col.editEndpoint || editEndpoint);
                    const isCopied = clip && clip.rowId === row[rowKey] && clip.colKey === col.key;
                    // Click-to-edit is bound to the WHOLE cell (not just the
                    // rendered content span) so there's no "dead space" in a
                    // wide column where a click falls through to the row's
                    // own onClick (e.g. opening a detail modal) instead of
                    // starting the inline edit — true Excel-like behaviour.
                    // tabIndex + onKeyDown gives editable cells Ctrl+C/Ctrl+V
                    // (copy a value, paste it onto another cell in the same
                    // column — saves immediately, no confirm step needed).
                    return (
                      <td key={col.key} style={{
                            textAlign: col.align,
                            cursor: isEditableCol && !editingThis ? "text" : undefined,
                            outline: isCopied ? "2px dashed var(--gold, #c9a227)" : undefined,
                            outlineOffset: isCopied ? "-2px" : undefined,
                          }}
                        tabIndex={isEditableCol && !editingThis ? 0 : undefined}
                        title={isEditableCol && !editingThis ? "Click to edit · Ctrl+C/Ctrl+V to copy-paste" : undefined}
                        onClick={isEditableCol && !editingThis ? (e) => { e.stopPropagation(); startEdit(row, col); } : undefined}
                        onKeyDown={isEditableCol && !editingThis ? (e) => copyPasteKeyDown(e, row, col) : undefined}>
                        {renderCell(row, col)}
                        {isCopied && <ClipboardCheck size={10} style={{ marginLeft: 4, color: "var(--gold, #c9a227)", verticalAlign: "middle" }} />}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12, fontSize: 13 }}>
        {shownTotal > limit || skip > 0 ? (
          <>
            <button className="btn ghost sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid={`${testid}-prev`}>← Prev</button>
            <span className="t-mut">{shownTotal === 0 ? 0 : skip + 1}–{skip + display.length} of {Number(shownTotal).toLocaleString()}</span>
            <button className="btn ghost sm" disabled={skip + limit >= shownTotal} onClick={() => setSkip(skip + limit)} data-testid={`${testid}-next`}>Next →</button>
          </>
        ) : <span className="t-mut">{Number(shownTotal).toLocaleString()} record{shownTotal !== 1 ? "s" : ""}</span>}
        <span style={{ flex: 1 }} />
        <label className="t-mut" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          Rows per page
          <select className="input" style={{ padding: "4px 8px", height: "auto" }} value={limit}
            onChange={(e) => setLimit(Number(e.target.value))} data-testid={`${testid}-page-size`}>
            {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
    </div>
  );
}
